/**
 * `store/queue` — the SET LIST slice of `spec.md` §13.9: "items, index, shuffle,
 * `shuffleSeed`, `repeatMode`, with optimistic local mutation + server reconcile".
 *
 * ## Shuffle is derived, never stored
 *
 * §13.9: shuffle is "a deterministic seeded Fisher-Yates over the queue's item ids using
 * `shuffleSeed` (a 32-bit integer stored server-side), so the shuffled order survives a
 * reload, a re-fetch and a cross-device resume. Toggling shuffle off restores the stored
 * `position` order without a server round-trip."
 *
 * So `items` is **always** in server `position` order and the shuffled order is a
 * selector ({@link selectPlayOrder}). Storing a shuffled copy would be a second source
 * of truth that a page re-fetch silently invalidates, and turning shuffle off would then
 * need the server to tell us what the original order was.
 *
 * The permutation is a plain Fisher-Yates driven by `mulberry32`, not `Math.random`: the
 * same seed must produce the same order on this phone, on a laptop, and after a reload,
 * which is the entire point of persisting a seed rather than an order.
 *
 * ## Optimistic mutation
 *
 * Every mutation applies locally first (a queue that lags a tap by a round trip feels
 * broken), then reconciles with what the server returns. On failure the previous list is
 * restored **wholesale** — no attempt is made to un-apply a diff, because a failed
 * reorder may have been rejected precisely because our idea of the list was stale.
 *
 * Optimistic rows carry a negative `id` and `pending: true`: `queue_items.id` is a real
 * positive rowid, so a negative id cannot collide with one, and the dashed-underline
 * `[data-state="pending"]` row treatment in `design-direction.md` §9.3 has something to
 * key off.
 */

import { create } from 'zustand';

import { REPEAT_MODES } from '@bl/shared';
import type { QueueHeader, QueueItem, QueueResponse, RepeatMode, TrackSummary } from '@bl/shared';

import type { ApiClient, ApiFailure, QueueMutationResponse } from '../lib/api';
import { api, toApiFailure } from '../lib/api';

/* ────────────────────────────────────────────────────────────────────────────
 * Seeded shuffle
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * `mulberry32` — a 32-bit-seeded PRNG with a full period over the seed space, ~10 lines,
 * no dependency, and identical output in every JS engine (all arithmetic is forced
 * through `Math.imul`/`>>>`, so no float or 64-bit differences can creep in).
 *
 * Cryptographic quality is irrelevant here and speed is not the constraint; **exact
 * reproducibility across devices** is the whole requirement.
 */
export function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Deterministic Fisher-Yates. Returns a new array; the input is never mutated.
 *
 * The classic backwards walk, `j = floor(random() * (i + 1))`, which is the only variant
 * that produces a uniform permutation. A `sort(() => random() - 0.5)` — the shape this
 * function exists to prevent — is neither uniform nor stable across engines, because the
 * result depends on the sort implementation.
 */
export function seededShuffle<T>(input: readonly T[], seed: number): T[] {
  const out = [...input];
  const random = mulberry32(seed);
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const a = out[i];
    const b = out[j];
    // `noUncheckedIndexedAccess`: both indices are in range by construction, but the
    // compiler cannot know that, and a non-null assertion is banned by lint.
    if (a === undefined || b === undefined) continue;
    out[i] = b;
    out[j] = a;
  }
  return out;
}

/* ────────────────────────────────────────────────────────────────────────────
 * State
 * ──────────────────────────────────────────────────────────────────────────── */

/** A queue row, plus the optimistic marker the server knows nothing about. */
export interface LocalQueueItem extends QueueItem {
  /** `true` while this row exists only locally. Absent on a server-confirmed row. */
  pending?: boolean;
}

export type QueueStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface QueueData {
  /** Always in server `position` order. The shuffled view is a selector. */
  items: LocalQueueItem[];
  /** Index into {@link QueueData.items}. `-1` means nothing is selected (§6.7). */
  index: number;
  shuffle: boolean;
  /** 32-bit. `0` is a legal seed and the DDL's default. */
  shuffleSeed: number;
  repeatMode: RepeatMode;
  /** Server-computed; recomputed locally after an optimistic edit. */
  durationRemainingS: number;
  status: QueueStatus;
  /** `true` while a mutation is in flight, so the sheet can dim rather than block. */
  syncing: boolean;
  error: ApiFailure | null;
}

/**
 * Every member is an arrow-typed property (`name: (args) => T`), not a method
 * signature (`name(args): T`). The two are call-compatible — the store below can (and
 * does) implement them with plain method shorthand — but only the arrow form tells
 * `@typescript-eslint/unbound-method` these are plain function values with no `this`
 * to lose, which matters because a screen selects one out of `state` and calls it
 * later (`const enqueueNext = useQueueStore((state) => state.enqueueNext)`) — exactly
 * the "extracted, not immediately called" shape the rule polices.
 */
export interface QueueActions {
  /** Adopt a server response verbatim. */
  hydrate: (response: QueueResponse) => void;
  load: () => Promise<void>;
  /** "Play all" / "play this playlist" — `PUT /api/queue`. */
  replace: (tracks: readonly TrackSummary[], index?: number) => Promise<void>;
  /** `POST /api/queue/items` with `mode: 'next'`. Optimistic. */
  enqueueNext: (tracks: readonly TrackSummary[]) => Promise<void>;
  /** `POST /api/queue/items` with `mode: 'end'`. Optimistic. */
  enqueueEnd: (tracks: readonly TrackSummary[]) => Promise<void>;
  removeItem: (itemId: number) => Promise<void>;
  reorder: (itemId: number, beforeItemId: number | null) => Promise<void>;
  clear: () => Promise<void>;
  /** Local only — the index is persisted by `store/persist`, not by a queue call. */
  setIndex: (index: number) => void;
  /** Advance to the next item per shuffle + repeat. Returns the new index, or `-1`. */
  advance: () => number;
  back: () => number;
  /** Local only, by design: §13.9 forbids a round trip here. */
  setShuffle: (shuffle: boolean) => void;
  setShuffleSeed: (seed: number) => void;
  setRepeatMode: (mode: RepeatMode) => void;
  cycleRepeatMode: () => RepeatMode;
  /** Mark the current item played, so the "remaining" figure stays honest. */
  markPlayed: (itemId: number) => void;
  reset: () => void;
}

export type QueueState = QueueData & QueueActions;

const EMPTY: QueueData = {
  items: [],
  index: -1,
  shuffle: false,
  shuffleSeed: 0,
  repeatMode: 'off',
  durationRemainingS: 0,
  status: 'idle',
  syncing: false,
  error: null,
};

/** `PUT /api/queue` uses `position = i * 1024`; local rows follow the same spacing. */
const POSITION_STEP = 1024;

/** Local ids count down from here so they can never collide with a real rowid. */
let localIdCursor = -1;

function nextLocalId(): number {
  localIdCursor -= 1;
  return localIdCursor;
}

/** Reset the local-id counter. Exported for tests that assert exact ids. */
export function resetLocalIds(): void {
  localIdCursor = -1;
}

function remainingSeconds(items: readonly LocalQueueItem[], index: number): number {
  let total = 0;
  for (let i = Math.max(0, index); i < items.length; i += 1) {
    const item = items[i];
    if (item === undefined || item.played) continue;
    total += item.track.durationS;
  }
  return total;
}

function toLocalItem(track: TrackSummary, position: number): LocalQueueItem {
  return { id: nextLocalId(), position, played: false, track, pending: true };
}

export interface QueueStoreDeps {
  client?: ApiClient;
}

export function createQueueStore(deps: QueueStoreDeps = {}) {
  const client = deps.client ?? api;

  return create<QueueState>()((set, get) => {
    /**
     * Run a mutation optimistically.
     *
     * `apply` produces the local state to show immediately; `call` performs the request;
     * the response either replaces the list (a full queue) or triggers a re-read (a bare
     * header — the row ids we invented locally are not the server's).
     */
    const mutate = async (
      apply: (state: QueueData) => Partial<QueueData>,
      call: () => Promise<QueueMutationResponse | QueueResponse | QueueHeader | void>,
    ): Promise<void> => {
      const before: QueueData = {
        items: get().items,
        index: get().index,
        shuffle: get().shuffle,
        shuffleSeed: get().shuffleSeed,
        repeatMode: get().repeatMode,
        durationRemainingS: get().durationRemainingS,
        status: get().status,
        syncing: get().syncing,
        error: get().error,
      };

      set({ ...apply(before), syncing: true, error: null });

      try {
        const response = await call();
        if (response !== undefined && 'items' in response) {
          get().hydrate(response);
          set({ syncing: false });
          return;
        }
        if (response !== undefined) {
          // A header tells us the count and the index but not the rows, and our local
          // rows carry invented ids. Re-read rather than keep a list we know is a guess.
          await get().load();
          set({ syncing: false });
          return;
        }
        set({ syncing: false });
      } catch (error) {
        set({
          items: before.items,
          index: before.index,
          durationRemainingS: before.durationRemainingS,
          syncing: false,
          status: 'error',
          error: toApiFailure(error),
        });
      }
    };

    return {
      ...EMPTY,

      hydrate(response): void {
        const items: LocalQueueItem[] = response.items.map((item) => ({ ...item }));
        set({
          items,
          index: response.index,
          durationRemainingS: response.durationRemainingS,
          status: 'ready',
          error: null,
        });
      },

      async load(): Promise<void> {
        set({ status: get().status === 'ready' ? 'ready' : 'loading', error: null });
        try {
          get().hydrate(await client.queue());
        } catch (error) {
          set({ status: 'error', error: toApiFailure(error) });
        }
      },

      async replace(tracks, index): Promise<void> {
        const trackIds = tracks.map((track) => track.id);
        const target = index ?? (trackIds.length > 0 ? 0 : -1);
        await mutate(
          () => {
            const items = tracks.map((track, i) => toLocalItem(track, i * POSITION_STEP));
            return { items, index: target, durationRemainingS: remainingSeconds(items, target) };
          },
          () => {
            const body: { trackIds: number[]; index?: number } = { trackIds };
            // §6.7 refuses an `index` that does not point inside `trackIds`, so an empty
            // replace sends none at all rather than `-1`.
            if (target >= 0 && target < trackIds.length) body.index = target;
            return client.replaceQueue(body);
          },
        );
      },

      async enqueueNext(tracks): Promise<void> {
        if (tracks.length === 0) return;
        await mutate(
          (state) => {
            const at = state.index < 0 ? 0 : state.index + 1;
            const head = state.items.slice(0, at);
            const tail = state.items.slice(at);
            // Midpoint spacing, matching the server's algorithm closely enough that the
            // optimistic order equals the reconciled order.
            const before = head.at(-1)?.position ?? 0;
            const after = tail[0]?.position ?? before + POSITION_STEP * (tracks.length + 1);
            const gap = (after - before) / (tracks.length + 1);
            const inserted = tracks.map((track, i) => toLocalItem(track, before + gap * (i + 1)));
            const items = [...head, ...inserted, ...tail];
            return { items, durationRemainingS: remainingSeconds(items, state.index) };
          },
          () => client.addQueueItems({ trackIds: tracks.map((t) => t.id), mode: 'next' }),
        );
      },

      async enqueueEnd(tracks): Promise<void> {
        if (tracks.length === 0) return;
        await mutate(
          (state) => {
            const last = state.items.at(-1)?.position ?? 0;
            const inserted = tracks.map((track, i) =>
              toLocalItem(track, last + POSITION_STEP * (i + 1)),
            );
            const items = [...state.items, ...inserted];
            return { items, durationRemainingS: remainingSeconds(items, state.index) };
          },
          () => client.addQueueItems({ trackIds: tracks.map((t) => t.id), mode: 'end' }),
        );
      },

      async removeItem(itemId): Promise<void> {
        await mutate(
          (state) => {
            const at = state.items.findIndex((item) => item.id === itemId);
            if (at < 0) return {};
            const items = state.items.filter((item) => item.id !== itemId);
            // Removing the current item leaves the index pointing at its successor;
            // removing one before it shifts the index down by one.
            let index = state.index;
            if (at < state.index) index -= 1;
            if (index >= items.length) index = items.length - 1;
            return { items, index, durationRemainingS: remainingSeconds(items, index) };
          },
          () => (itemId > 0 ? client.removeQueueItem(itemId) : Promise.resolve(undefined)),
        );
      },

      async reorder(itemId, beforeItemId): Promise<void> {
        if (itemId === beforeItemId) return;
        await mutate(
          (state) => {
            const from = state.items.findIndex((item) => item.id === itemId);
            if (from < 0) return {};
            const moved = state.items[from];
            if (moved === undefined) return {};
            const currentId = state.items[state.index]?.id;
            const rest = state.items.filter((item) => item.id !== itemId);
            const to =
              beforeItemId === null ? rest.length : rest.findIndex((i) => i.id === beforeItemId);
            const at = to < 0 ? rest.length : to;
            const items = [...rest.slice(0, at), moved, ...rest.slice(at)];
            // The index follows the *track*, not the slot: a drag must never change what
            // is playing.
            const index =
              currentId === undefined ? state.index : items.findIndex((i) => i.id === currentId);
            return { items, index, durationRemainingS: remainingSeconds(items, index) };
          },
          () =>
            itemId > 0 ? client.reorderQueue({ itemId, beforeItemId }) : Promise.resolve(undefined),
        );
      },

      async clear(): Promise<void> {
        await mutate(
          () => ({ items: [], index: -1, durationRemainingS: 0 }),
          async () => {
            await client.clearQueue();
          },
        );
      },

      setIndex(index): void {
        const items = get().items;
        const clamped = items.length === 0 ? -1 : Math.min(Math.max(index, -1), items.length - 1);
        set({ index: clamped, durationRemainingS: remainingSeconds(items, clamped) });
      },

      advance(): number {
        const next = selectNextIndex(get());
        get().setIndex(next);
        return next;
      },

      back(): number {
        const previous = selectPreviousIndex(get());
        get().setIndex(previous);
        return previous;
      },

      setShuffle(shuffle): void {
        set({ shuffle });
      },
      setShuffleSeed(seed): void {
        set({ shuffleSeed: seed >>> 0 });
      },
      setRepeatMode(mode): void {
        set({ repeatMode: mode });
      },
      cycleRepeatMode(): RepeatMode {
        const at = REPEAT_MODES.indexOf(get().repeatMode);
        const next = REPEAT_MODES[(at + 1) % REPEAT_MODES.length] ?? 'off';
        set({ repeatMode: next });
        return next;
      },

      markPlayed(itemId): void {
        const items = get().items.map((item) =>
          item.id === itemId ? { ...item, played: true } : item,
        );
        set({ items, durationRemainingS: remainingSeconds(items, get().index) });
      },

      reset(): void {
        set({ ...EMPTY });
      },
    };
  });
}

/** The app-wide queue slice. */
export const useQueueStore = createQueueStore();

/* ────────────────────────────────────────────────────────────────────────────
 * Selectors
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The play order as item ids: the stored order, or its seeded permutation while shuffle
 * is on. Toggling shuffle off restores the stored order with no request, because the
 * stored order was never overwritten.
 */
export const selectPlayOrder = (state: QueueData): number[] => {
  const ids = state.items.map((item) => item.id);
  return state.shuffle ? seededShuffle(ids, state.shuffleSeed) : ids;
};

export const selectCurrentItem = (state: QueueData): LocalQueueItem | null =>
  state.items[state.index] ?? null;

export const selectCurrentTrack = (state: QueueData): TrackSummary | null =>
  selectCurrentItem(state)?.track ?? null;

/**
 * The index of the item that plays next, or `-1` when the set is over.
 *
 * `repeatMode: 'one'` returns the current index — the engine re-seeks rather than
 * re-selecting, but the queue's answer to "what is next" is still this track.
 */
export const selectNextIndex = (state: QueueData): number => {
  if (state.items.length === 0) return -1;
  if (state.repeatMode === 'one') return state.index;

  const order = selectPlayOrder(state);
  const currentId = state.items[state.index]?.id;
  const at = currentId === undefined ? -1 : order.indexOf(currentId);

  if (at + 1 < order.length) {
    const nextId = order[at + 1];
    return nextId === undefined ? -1 : state.items.findIndex((item) => item.id === nextId);
  }
  if (state.repeatMode === 'all') {
    const firstId = order[0];
    return firstId === undefined ? -1 : state.items.findIndex((item) => item.id === firstId);
  }
  return -1;
};

/** The mirror of {@link selectNextIndex}. `repeatMode: 'off'` stops at the first item. */
export const selectPreviousIndex = (state: QueueData): number => {
  if (state.items.length === 0) return -1;
  if (state.repeatMode === 'one') return state.index;

  const order = selectPlayOrder(state);
  const currentId = state.items[state.index]?.id;
  const at = currentId === undefined ? -1 : order.indexOf(currentId);

  if (at > 0) {
    const previousId = order[at - 1];
    return previousId === undefined ? -1 : state.items.findIndex((item) => item.id === previousId);
  }
  if (state.repeatMode === 'all') {
    const lastId = order.at(-1);
    return lastId === undefined ? -1 : state.items.findIndex((item) => item.id === lastId);
  }
  return state.index;
};

/** The track that follows the current one, for the media-token batch of §9.5. */
export const selectNextTrack = (state: QueueData): TrackSummary | null => {
  const next = selectNextIndex(state);
  if (next < 0 || next === state.index) return null;
  return state.items[next]?.track ?? null;
};

/**
 * The pair `[current, next]` — exactly the argument `api.ensureMediaUrls` wants, so the
 * URL for the successor is minted long before `ended` fires (§9.5).
 */
export const selectMediaTokenBatch = (state: QueueData): number[] => {
  const ids: number[] = [];
  const current = selectCurrentTrack(state);
  if (current !== null) ids.push(current.id);
  const next = selectNextTrack(state);
  if (next !== null && next.id !== current?.id) ids.push(next.id);
  return ids;
};

export const selectQueueCount = (state: QueueData): number => state.items.length;

/** `true` when at least one row is still local-only. */
export const selectHasPendingRows = (state: QueueData): boolean =>
  state.items.some((item) => item.pending === true);

/** The queue's contribution to the resume snapshot (§13.7): ids, not rows. */
export const selectQueueTrackIds = (state: QueueData): number[] =>
  state.items.map((item) => item.track.id);
